'use client';

import { t } from 'mobx-state-tree';

import * as M from './properties';
import { PropertyType } from './propertyType';

import type { PropertyBase } from './properties/propertyBase';
import type { IAssetFile, IEditableObject } from '@/editor/store/models';
import type { Instance, SnapshotOrInstance } from 'mobx-state-tree';
import type { IAnyModelType } from 'mobx-state-tree';


/* Containers */
export type IObjectPropertyLike = Instance<typeof PropertyBase> & { type: PropertyType.Object };
export type IObjectPropertyLikeSnapshot = SnapshotOrInstance<typeof PropertyBase> & { type: PropertyType.Object };
export type IArrayPropertyLike = Instance<typeof PropertyBase> & { type: PropertyType.Array };
export type IArrayPropertyLikeSnapshot = SnapshotOrInstance<typeof PropertyBase> & { type: PropertyType.Array };
/* Special */
export type IErrorProperty = Instance<typeof M.ErrorProperty>;

// Members are wrapped in `t.late` so the union does not eagerly read the
// `./properties` barrel exports at module-evaluation time. Under CommonJS module
// resolution (e.g. the `tsx` test runner) the barrel can be mid-initialization
// when this file is first required, which would otherwise leave members `undefined`.
// `t.late<T>(() => T)` returns `T`, so the inferred union types are unchanged.
export const PropertyModel = t.union(
  /* Basic */
  t.late(() => M.SelectProperty),
  t.late(() => M.MultiSelectProperty),
  t.late(() => M.NumberProperty),
  t.late(() => M.ColorProperty),
  t.late(() => M.StringProperty),
  t.late(() => M.BooleanProperty),
  t.late(() => M.Vector2Property),
  t.late(() => M.Vector3Property),
  t.late(() => M.Vector4Property),
  t.late(() => M.EulerProperty),
  /* Custom */
  t.late(() => M.AssetProperty),
  t.late(() => M.ActorReferenceProperty),
  t.late(() => M.ComponentReferenceProperty),
  t.late(() => M.SpawnableProperty),
  /* Actions */
  t.late(() => M.ButtonProperty),
  /* Special */
  t.late(() => M.ErrorProperty),
);
export type IPropertyModel = Instance<typeof PropertyModel>;
export type IProperty = IPropertyModel | IObjectPropertyLike | IArrayPropertyLike;
export type IPropertySnapshot = SnapshotOrInstance<typeof PropertyModel> | IObjectPropertyLikeSnapshot | IArrayPropertyLikeSnapshot;

export const AnyProperty = t.union(PropertyModel, t.late((): IAnyModelType => M.ObjectProperty), t.late((): IAnyModelType => M.ArrayProperty));

export type IObjectValue = IEditableObject | IAssetFile | object | null;

// Re-export PropertyType for convenience
export { PropertyType };